import type { RawComponent } from './editorTypes';

// Students need not have answered: a question is locked once they can see it.
export const STRUCTURAL_EDIT_LOCK_REASON =
  "Can't change while students can see this question";

export const VISIBLE_QUESTION_EDIT_LOCK_REASON =
  'Cannot change while this section is visible to students';

interface PreLectureRestrictionTimeline {
  isPreLectureClosed: boolean;
  isPreLectureVisible: boolean;
}

interface HomeworkRestrictionTimeline {
  isHomeworkClosed: boolean;
  isHomeworkVisible: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim();

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function hasQuestionAttempts(statsValue: unknown): boolean {
  if (!isRecord(statsValue)) {
    return false;
  }

  const questionStatsValue =
    statsValue.question_stats ?? statsValue.questionStats;
  const questionStats = Array.isArray(questionStatsValue)
    ? questionStatsValue.filter(isRecord)
    : [];

  return questionStats.some((questionStat) => {
    const attempts = normalizeNumber(
      questionStat.current_num_attempts ?? questionStat.currentNumAttempts,
    ) ?? 0;

    if (attempts > 0) {
      return true;
    }

    return hasQuestionAttempts(
      questionStat.question_set_stats ?? questionStat.questionSetStats,
    );
  });
}

export function arePreLectureEditsRestricted(
  timeline: PreLectureRestrictionTimeline,
) {
  return timeline.isPreLectureVisible || timeline.isPreLectureClosed;
}

export function areHomeworkEditsRestricted(
  timeline: HomeworkRestrictionTimeline,
) {
  return timeline.isHomeworkVisible || timeline.isHomeworkClosed;
}

/**
 * @deprecated Use arePreLectureEditsRestricted or areHomeworkEditsRestricted
 * for section-specific question edit restrictions.
 */
export function areStructuralEditsRestricted(component: RawComponent): boolean {
  const isLive =
    component.is_live === true || component.isLive === true;

  if (!isLive) {
    return false;
  }

  const sections = Array.isArray(component.sections) ? component.sections : [];

  return sections.some((section) => {
    if (hasQuestionAttempts(section.stats)) {
      return true;
    }

    const questionSection =
      section.question_section ?? section.questionSection;
    const preLectureQuestionSection =
      section.pre_lecture_question_section ?? section.preLectureQuestionSection;

    return (
      hasQuestionAttempts(questionSection?.stats) ||
      hasQuestionAttempts(preLectureQuestionSection?.stats)
    );
  });
}
